/* ============================================================================
   Ohr Labs — Grid Fit (grid-fit.js)  ·  the --cell quantiser + grid renderer
   ----------------------------------------------------------------------------
   ONE fluid unit drives the whole page. fitCell() picks a whole number of columns
   for the viewport width and sets --cell = viewportWidth / cols (intentionally
   fractional, e.g. 39.43px) so a whole number of cells always fits — never a
   partial cell on the right edge. drawGrid() draws the grid as an SVG path on
   whole device pixels so the hairlines stay crisp at any cell size.

   Ported from the design prototype's inline script, minus the author-only tooling
   (Tweaks panel, pixel painter, host postMessage protocol).
   ============================================================================ */
(function () {
  // Fluid fixed-count grid: target ~2.5rem (40px at a 16px root), but pick a whole
  // column count + fractional cell so a whole multiple always fits the width.
  // Only recompute on WIDTH change — height-only resizes (scroll, address bar,
  // scrollbar toggle) are ignored so the grid never shifts while scrolling.
  function fitCell() {
    var rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    var targetPx = 2.5 * rootPx;
    var vw = document.documentElement.clientWidth;
    if (fitCell._lastW === vw) return;
    fitCell._lastW = vw;
    var cols = Math.max(1, Math.round(vw / targetPx));
    var cell = vw / cols;
    document.documentElement.style.setProperty('--cell', cell + 'px');
    drawGrid();
    if (window.gridSnap) window.gridSnap();
  }

  // Grid as ONE SVG path of thin rects, positioned on whole device pixels and one
  // device pixel thick → constant crisp hairlines at ANY (fractional) cell size, while
  // cells still fit a whole number of columns. Vector, so the browser only rasterises
  // the visible tiles — a page-sized canvas bitmap cost ~230MB on a long Retina page
  // and was re-allocated on every re-snap. Only rebuilt when the geometry changes.
  var drawnKey = '';
  function drawGrid() {
    var svg = document.getElementById('grid-lines');
    if (!svg) return;
    var path = svg.firstElementChild;
    if (!path) return;
    var cell = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--cell')) || 40;
    var dpr = window.devicePixelRatio || 1;
    var w = document.documentElement.clientWidth;
    svg.style.height = '0px';   // collapse before measuring so the grid can't inflate scrollHeight
    var h = document.documentElement.scrollHeight;   // grid ends with content, never below the footer
    svg.style.width = w + 'px';
    svg.style.height = h + 'px';
    var key = [w, h, cell, dpr].join('|');
    if (key !== drawnKey) {
      drawnKey = key;
      svg.setAttribute('width', w);
      svg.setAttribute('height', h);
      var px = 1 / dpr;                 // one device pixel, in CSS px
      var cellDev = cell * dpr;
      var d = [];
      var cols = Math.round(w / cell);
      for (var i = 0; i <= cols; i++) d.push('M' + (Math.round(i * cellDev) / dpr) + ' 0h' + px + 'v' + h + 'h-' + px + 'z');
      var rows = Math.ceil(h / cell);
      for (var j = 0; j <= rows; j++) d.push('M0 ' + (Math.round(j * cellDev) / dpr) + 'h' + w + 'v' + px + 'h-' + w + 'z');
      path.setAttribute('d', d.join(''));
    }
    // Grid is live — retire the CSS-gradient placeholder grid (see grid.css body rule).
    document.body.classList.add('grid-canvas-on');
  }
  window.drawGrid = drawGrid;
  window.fitCell = fitCell;

  // Bootstrap + settle. The Shopify port settles LATER than opening the local
  // design file: the web-font swap reflow and late CDN images grow block heights
  // AFTER the initial snap, leaving blocks half-a-cell off-grid (they cascade).
  // So re-snap after load, after fonts, on every late image load, and on a couple
  // of delayed passes. Re-snapping is idempotent, so extra passes are harmless.
  function settle() { if (window.gridSnap) window.gridSnap(); if (window.drawGrid) window.drawGrid(); }
  var settleT;
  function settleSoon() { clearTimeout(settleT); settleT = setTimeout(settle, 120); }

  fitCell();
  window.addEventListener('load', function () {
    fitCell(); drawGrid(); settle();
    setTimeout(settle, 400);
    setTimeout(settle, 1200);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { fitCell(); drawGrid(); settle(); });
  }
  // Late-loading images change block heights after the initial snap → re-snap.
  // (img 'load' doesn't bubble, so listen in the capture phase.)
  document.addEventListener('load', function (e) {
    if (e.target && e.target.tagName === 'IMG') settleSoon();
  }, true);

  // Recompute only after a resize settles, and only when WIDTH changed. Changing
  // --cell reflows every cell-based dimension, which settles a beat after the first
  // snap — so run a follow-up settle to catch the final heights.
  var t;
  window.addEventListener('resize', function () {
    if (document.documentElement.clientWidth === fitCell._lastW) return;
    clearTimeout(t);
    t = setTimeout(function () { fitCell(); drawGrid(); settle(); setTimeout(settle, 250); }, 160);
  });
})();
